import { Stack, TableCell, TableRow } from '@mui/material'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import {
    formatBigMoney,
    formatDateYYYYMMDD,
    formatMoney,
    formatNumber,
} from '../../../../packages/utils/numberFormatters'
import { EnrichedIssueEntity, IssueEntity } from '../../../../types/IssueEntity'
import IssueTypeLabels from '../../../../components/entities/IssueTypeLabels'
import { hasSourceOfType } from '../../../../components/utils/calculateIssueStatus'
import { NameChangeRowItem } from 'types/CompanyEntity'
import { IssueType } from '@finance-lab/utils/types'
import { TFunction } from 'i18next'

interface IssueHistoryTableRowProps {
    date: string | null
    issue: EnrichedIssueEntity
    locale: string
    multipleInstrumentsExists: boolean
    splitAdjust: boolean
    isSmallScreen: boolean
    nameChangeRows: NameChangeRowItem[]
}

const getIssueTypeLabel = (type: IssueType, earnout: IssueEntity['earnout'], t: TFunction) => {
    if (type === 'Non-cash issue' && earnout) {
        return t('entities.issueType.earnout')
    }
    if (type === 'Guarantor fee issue') return t('pages.companies.guarantorIssue')
    if (type === 'ABB Directed issue') return t('pages.companies.abbDirectedIssue')
    return t(`entities.issueType.values.${type}`)
}

const getLinkedGuarantorIssues = (issue: IssueEntity) => {
    if (issue.linkedIssues.length === 0) {
        return []
    }

    const linkedGuarantorIssues = issue.linkedIssues.reduce((acc: Omit<IssueEntity, 'linkedIssues'>[], curr) => {
        if (curr.issues.length === 0) {
            return acc
        }

        const guarantorIssues = curr.issues.filter((issue) => issue.type === 'Guarantor fee issue')

        acc.push(...guarantorIssues)

        return acc
    }, [])

    return linkedGuarantorIssues
}

export const IssueHistoryTableRow = ({
    date,
    issue,
    locale,
    multipleInstrumentsExists = false,
    splitAdjust = false,
    isSmallScreen,
    nameChangeRows,
}: IssueHistoryTableRowProps) => {
    const { t } = useTranslation()

    const unitSubPrice = issue.unitSubPrice ?? 0
    const sharesInUnit = issue.sharesInUnit ?? 1
    const unitSubTotal = issue.unitSubTotal ?? 0
    const subPricePerShare = sharesInUnit === 0 ? null : unitSubPrice / sharesInUnit
    const sumSharesSubscribed = sharesInUnit === 0 ? null : unitSubTotal * sharesInUnit
    const shouldShowOverAllotmentAllocation =
        issue.overAllotmentAllocation !== null && issue.overAllotmentAllocation > 0
    const shouldShowOverAllotmentAllocationSecondary =
        issue.overAllotmentAllocationSecondary !== null && issue.overAllotmentAllocationSecondary > 0
    const shouldShowSecondarySub = issue.secondarySub !== null && issue.secondarySub > 0

    const linkedGuarantorIssues = getLinkedGuarantorIssues(issue)

    const isCancelled = hasSourceOfType(issue, 'Cancellation')

    const numberOfShares = (() => {
        if (isCancelled) {
            return t('pages.warrants.status.cancelled')
        } else if (issue.totalOfferedUnits === 0) {
            return t('pages.companies.history.zeroNewShares')
        } else if (sumSharesSubscribed === 0) {
            return t('site.notCommunicatedYet')
        } else if (sumSharesSubscribed === null) {
            return '-'
        }
        return formatNumber(sumSharesSubscribed / (splitAdjust ? issue.splitAdjustment : 1), {
            locale,
            decimals: 0,
        })
    })()

    const oldestNameChange =
        nameChangeRows.length > 0
            ? nameChangeRows.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())[0]
            : null

    return (
        <>
            <TableRow
                sx={{
                    fontStyle: isCancelled ? 'italic' : 'normal',
                    ...(shouldShowOverAllotmentAllocation ||
                    shouldShowSecondarySub ||
                    shouldShowOverAllotmentAllocationSecondary ||
                    linkedGuarantorIssues.length > 0
                        ? {
                              '& .MuiTableCell-root': {
                                  borderBottom: 0,
                              },
                          }
                        : {}),
                }}
            >
                <TableCell>{formatDateYYYYMMDD(date, { locale })}</TableCell>
                {multipleInstrumentsExists && !isSmallScreen ? <TableCell>{issue.instrumentSymbol}</TableCell> : null}
                <TableCell>
                    <Stack direction='row' spacing={2}>
                        <IssueTypeLabels
                            hasWarrants={!!issue.hasWarrants}
                            isRto={!!issue.rto}
                            isSpac={!!issue.spac}
                            issueType={issue.type}
                            sharesInUnit={issue.sharesInUnit}
                            unitExtraContent={issue.unitExtraContent}
                        />
                        <Link to={`/issues/${issue.id}`}>
                            {getIssueTypeLabel(issue.type, issue.earnout, t)}
                            {issue.type === 'Non-cash issue'
                                ? ` (${issue.noncashConsideration})`
                                : issue.type === 'IPO' && oldestNameChange?.item
                                  ? ` (${oldestNameChange.item.fromName})`
                                  : null}
                        </Link>
                    </Stack>
                </TableCell>
                {!isSmallScreen ? (
                    <>
                        <TableCell>
                            {subPricePerShare
                                ? formatMoney(subPricePerShare * (splitAdjust ? issue.splitAdjustment : 1), {
                                      locale,
                                  })
                                : '-'}
                        </TableCell>
                        <TableCell>{numberOfShares}</TableCell>
                    </>
                ) : null}
                <TableCell>
                    {sumSharesSubscribed && subPricePerShare
                        ? formatBigMoney(sumSharesSubscribed * subPricePerShare, { locale, decimals: 0, tooltip: true })
                        : '-'}
                </TableCell>
            </TableRow>
            {shouldShowOverAllotmentAllocation && (
                <TableRow
                    sx={{
                        '& .MuiTableCell-root': {
                            paddingTop: 0,
                            ...(shouldShowSecondarySub ||
                            shouldShowOverAllotmentAllocationSecondary ||
                            linkedGuarantorIssues.length > 0
                                ? {
                                      borderBottom: 0,
                                  }
                                : {}),
                        },
                    }}
                >
                    <TableCell />
                    {multipleInstrumentsExists && !isSmallScreen ? <TableCell /> : null}
                    <TableCell>{t('pages.companies.history.overAllotment')}</TableCell>
                    {!isSmallScreen ? (
                        <>
                            <TableCell />
                            <TableCell>
                                {formatNumber(
                                    ((issue.overAllotmentAllocation as number) * sharesInUnit) /
                                        (splitAdjust ? issue.splitAdjustment : 1),
                                    { locale, decimals: 0 },
                                )}
                            </TableCell>
                        </>
                    ) : null}
                    <TableCell>
                        {subPricePerShare
                            ? formatBigMoney(
                                  (issue.overAllotmentAllocation as number) * sharesInUnit * subPricePerShare,
                                  {
                                      locale,
                                      decimals: 0,
                                      tooltip: true,
                                  },
                              )
                            : '-'}
                    </TableCell>
                </TableRow>
            )}
            {shouldShowSecondarySub && (
                <TableRow
                    sx={{
                        '& .MuiTableCell-root': {
                            paddingTop: 0,
                            ...(shouldShowOverAllotmentAllocationSecondary || linkedGuarantorIssues.length > 0
                                ? {
                                      borderBottom: 0,
                                  }
                                : {}),
                        },
                    }}
                >
                    <TableCell />
                    {multipleInstrumentsExists && !isSmallScreen ? <TableCell /> : null}
                    <TableCell>{t('pages.companies.history.existingShares')}</TableCell>
                    {!isSmallScreen ? (
                        <>
                            <TableCell />
                            <TableCell>
                                {formatNumber(
                                    ((issue.secondarySub as number) * sharesInUnit) /
                                        (splitAdjust ? issue.splitAdjustment : 1),
                                    { locale, decimals: 0 },
                                )}
                            </TableCell>
                        </>
                    ) : null}
                    <TableCell>
                        {unitSubPrice ? (
                            <>
                                (
                                {formatBigMoney((issue.secondarySub as number) * unitSubPrice, {
                                    locale,
                                    decimals: 0,
                                    tooltip: true,
                                })}
                                )
                            </>
                        ) : (
                            '-'
                        )}
                    </TableCell>
                </TableRow>
            )}
            {shouldShowOverAllotmentAllocationSecondary && (
                <TableRow
                    sx={{
                        '& .MuiTableCell-root': {
                            paddingTop: 0,
                            ...(linkedGuarantorIssues.length > 0
                                ? {
                                      borderBottom: 0,
                                  }
                                : {}),
                        },
                    }}
                >
                    <TableCell />
                    {multipleInstrumentsExists && !isSmallScreen ? <TableCell /> : null}
                    <TableCell>{t('pages.companies.history.overAllotmentExisting')}</TableCell>
                    {!isSmallScreen ? (
                        <>
                            <TableCell />
                            <TableCell>
                                {formatNumber(
                                    ((issue.overAllotmentAllocationSecondary as number) * sharesInUnit) /
                                        (splitAdjust ? issue.splitAdjustment : 1),
                                    {
                                        locale,
                                        decimals: 0,
                                    },
                                )}
                            </TableCell>
                        </>
                    ) : null}
                    <TableCell>
                        (
                        {unitSubPrice
                            ? formatBigMoney((issue.overAllotmentAllocationSecondary as number) * unitSubPrice, {
                                  locale,
                                  decimals: 0,
                                  tooltip: true,
                              })
                            : '-'}
                        )
                    </TableCell>
                </TableRow>
            )}
            {linkedGuarantorIssues.map(({ id, unitSubTotal }) => (
                <TableRow
                    key={id}
                    sx={{
                        '& .MuiTableCell-root': {
                            paddingTop: 0,
                        },
                    }}
                >
                    <TableCell />
                    {multipleInstrumentsExists && !isSmallScreen ? <TableCell /> : null}
                    <TableCell>{t('entities.issueType.values.Guarantor fee issue')}</TableCell>
                    {!isSmallScreen ? (
                        <>
                            <TableCell />
                            <TableCell>
                                {unitSubTotal !== null && unitSubTotal > 0
                                    ? formatNumber(unitSubTotal / (splitAdjust ? issue.splitAdjustment : 1), {
                                          locale,
                                          decimals: 0,
                                      })
                                    : '-'}
                            </TableCell>
                        </>
                    ) : null}
                    <TableCell>
                        {unitSubTotal !== null && unitSubTotal > 0 && subPricePerShare
                            ? formatBigMoney(unitSubTotal * subPricePerShare, { locale, decimals: 0, tooltip: true })
                            : '-'}
                    </TableCell>
                </TableRow>
            ))}
        </>
    )
}
